import {
  AVATAR_MAX_BYTES,
  AVATAR_MAX_PX,
  BLURHASH_MAX_CHARS,
  MEDIA_IMAGE_CONTENT_TYPES,
  MEDIA_MAX_BYTES,
  MEDIA_MAX_PX,
  VIDEO_MAX_BYTES,
  VIDEO_MAX_MS,
  mediaLimitViolation,
  type MediaCandidate,
  type MediaLimitCode,
  type MediaTarget,
} from '@astrally/shared';

/**
 * CLIENT MEDIA PIPELINE — resize, encode, blur, probe.
 *
 * No dependencies beyond the browser: a canvas, an ImageBitmap and a video
 * element do all of it. Nothing here talks to Firebase; the caller asks the
 * `mediaGrantUpload` callable for a path and uploads the Blob this module
 * returns.
 *
 * Two jobs, and they are not the same job:
 *
 *  1. COST. Every byte uploaded is a byte served back, repeatedly, forever.
 *     Resizing happens here, on the device, before anything is spent —
 *     deliberately instead of a server-side transcoding function, which would
 *     add a compute bill, a cold start and an operational surface to do work a
 *     phone does for free.
 *
 *  2. THE LOCKED STATE. Every upload also produces a ~28-character BlurHash
 *     that is stored on the post/message document. A non-Pro viewer renders
 *     the blur from that string and never requests the file at all. That is
 *     what makes the paywall free to enforce; what makes it *true* is
 *     storage.rules, which refuses the object to anyone who is not Pro.
 *
 * NOTHING HERE IS A SECURITY CONTROL. Every check in this file can be removed
 * by a modified client. They exist so honest users get a clear refusal instead
 * of a failed upload, and so the common case never reaches the server at all.
 * The enforcement is storage.rules plus the upload-grant callable.
 */

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Client-only failures, on top of the shared limit codes. */
export type MediaClientErrorCode =
  | 'not-an-image'
  | 'not-a-video'
  | 'input-too-large'
  | 'image-unreadable'
  | 'video-unreadable'
  | 'encode-failed'
  | 'canvas-unavailable';

export type MediaErrorCode = MediaClientErrorCode | MediaLimitCode;

/**
 * A refusal the UI can show verbatim.
 *
 * `code` is the stable contract — screens should map it to an i18n key in
 * app/src/i18n/en.ts. `message` is an English fallback so a screen that has
 * not been translated yet still says something true and specific rather than
 * "Upload failed". Nothing in this file ever silently shrinks, crops or
 * truncates past a limit: the user is told what the limit is and what to do.
 */
export class MediaError extends Error {
  constructor(
    readonly code: MediaErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'MediaError';
  }
}

const mb = (bytes: number): string => `${(bytes / 1_000_000).toFixed(1)}MB`;
const kb = (bytes: number): string => `${Math.round(bytes / 1000)}KB`;

/**
 * Ceiling on what we will even attempt to DECODE. A 50MP RAW-ish JPEG expands
 * to hundreds of megabytes of bitmap and will take the tab down on a low-end
 * Android before any of our own limits are consulted, so this guard runs first
 * and refuses on file size alone.
 */
const DECODE_INPUT_MAX_BYTES = 25_000_000;

// ---------------------------------------------------------------------------
// Image downscaling
// ---------------------------------------------------------------------------

export interface PreparedImage {
  /** Re-encoded bytes, ready to upload. */
  blob: Blob;
  /** Actual type of `blob` — WebP everywhere that supports it, else JPEG. */
  contentType: string;
  width: number;
  height: number;
  bytes: number;
  /** Computed from the downscaled pixels; store this on the document. */
  blurhash: string;
}

/**
 * Quality ladder for the re-encode. Descending, and it stops at the first
 * rung that fits — most photos land on the first or second.
 */
const QUALITY_LADDER = [0.82, 0.72, 0.62, 0.52, 0.44, 0.36] as const;

/** Dimension fallback when even the lowest quality misses the byte target. */
const SHRINK_FACTOR = 0.8;
const MAX_SHRINKS = 3;
const MIN_EDGE_PX = 96;

/**
 * Resize to `maxPx` on the longest edge, encode to WebP, and step the quality
 * down until the result is under `maxBytes`.
 *
 * ORIENTATION is preserved rather than reset: a portrait photo from a phone is
 * usually landscape pixels plus an EXIF rotation flag, and a canvas drawn from
 * the raw pixels loses the flag and turns the photo on its side. The decode
 * asks for `imageOrientation: 'from-image'`, which applies the flag while
 * decoding; the <img> fallback relies on the browser default
 * `image-orientation: from-image`, which every currently supported browser
 * honours when drawing to a canvas.
 *
 * Throws MediaError rather than returning something over the limit.
 */
export async function downscaleImage(
  file: Blob,
  maxPx: number,
  maxBytes: number,
): Promise<PreparedImage> {
  if (!file.type.startsWith('image/')) {
    throw new MediaError('not-an-image', 'That file isn’t an image. Choose a JPG, PNG or WebP.');
  }
  if (file.type === 'image/svg+xml') {
    throw new MediaError(
      'not-an-image',
      'SVG files aren’t supported. Choose a photo — JPG, PNG or WebP.',
    );
  }
  if (file.size > DECODE_INPUT_MAX_BYTES) {
    throw new MediaError(
      'input-too-large',
      `That image is ${mb(file.size)}. Pick one under ${mb(DECODE_INPUT_MAX_BYTES)} — anything larger can crash the app before it can be resized.`,
    );
  }

  const source = await decodeOriented(file);
  try {
    const longest = Math.max(source.width, source.height);
    const baseScale = Math.min(1, maxPx / longest);
    const contentType = supportsWebp() ? 'image/webp' : 'image/jpeg';

    for (let shrink = 0; shrink <= MAX_SHRINKS; shrink += 1) {
      const scale = baseScale * SHRINK_FACTOR ** shrink;
      const width = Math.max(1, Math.round(source.width * scale));
      const height = Math.max(1, Math.round(source.height * scale));
      if (shrink > 0 && Math.max(width, height) < MIN_EDGE_PX) break;

      const canvas = drawToCanvas(source.image, width, height);
      for (const quality of QUALITY_LADDER) {
        const blob = await toBlob(canvas, contentType, quality);
        if (blob.size <= maxBytes) {
          return {
            blob,
            contentType,
            width,
            height,
            bytes: blob.size,
            blurhash: blurhashFor(canvas),
          };
        }
      }
    }

    // Reachable in practice only for pathological input — a full-frame photo
    // of noise, or an avatar target that a detailed image cannot meet at 256px.
    throw new MediaError(
      'encode-failed',
      `That image can’t be compressed under ${kb(maxBytes)} without ruining it. Try a simpler photo, or crop it first.`,
    );
  } finally {
    source.release();
  }
}

/** Profile picture: 256px, under 30KB. Discovery renders these by the dozen. */
export function prepareAvatar(file: Blob): Promise<PreparedImage> {
  return downscaleImage(file, AVATAR_MAX_PX, AVATAR_MAX_BYTES);
}

/** Feed or chat image: 1280px longest edge, under 300KB. */
export function preparePostImage(file: Blob): Promise<PreparedImage> {
  return downscaleImage(file, MEDIA_MAX_PX, MEDIA_MAX_BYTES);
}

// ---------------------------------------------------------------------------
// Video
// ---------------------------------------------------------------------------

export interface ProbedVideo {
  durationMs: number;
  width: number;
  height: number;
  /** From the first frame — this is what a non-Pro viewer sees. */
  blurhash: string;
  bytes: number;
  contentType: string;
}

/** Decode/metadata/seek deadline. A bad file must not hang a screen forever. */
const PROBE_TIMEOUT_MS = 12_000;

/**
 * Read a video's duration and first frame.
 *
 * The duration check is here and the byte check is here, and NEITHER is
 * enforcement: the callable re-checks the declared numbers and storage.rules
 * caps the stored size, but nothing except a transcoder can measure the length
 * of an uploaded video server-side. The byte cap is what actually bounds it —
 * see VIDEO_MAX_BYTES in packages/shared/src/types/media.ts.
 *
 * The video is NOT re-encoded. It is stored exactly as recorded, so a clip
 * over the byte cap is refused rather than shrunk, and the message says to
 * record at a lower quality.
 */
export async function probeVideo(file: Blob): Promise<ProbedVideo> {
  if (!file.type.startsWith('video/')) {
    throw new MediaError('not-a-video', 'That file isn’t a video. Choose an MP4 or MOV.');
  }
  if (file.size > VIDEO_MAX_BYTES) {
    throw new MediaError(
      'video-too-large',
      `That clip is ${mb(file.size)}; the limit is ${mb(VIDEO_MAX_BYTES)}. Record it at 720p instead of 1080p, or trim it — videos are stored exactly as recorded, so we can’t shrink it for you.`,
    );
  }

  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = url;

  try {
    await once(video, 'loadedmetadata', PROBE_TIMEOUT_MS, 'video-unreadable');

    const durationMs = Math.round(video.duration * 1000);
    if (!Number.isFinite(durationMs) || durationMs <= 0) {
      throw new MediaError(
        'video-duration-unknown',
        'We couldn’t read how long that clip is, so we can’t check it against the 15-second limit. Try an MP4.',
      );
    }
    if (durationMs > VIDEO_MAX_MS) {
      throw new MediaError(
        'video-too-long',
        `That clip is ${(durationMs / 1000).toFixed(1)} seconds. Videos have to be ${VIDEO_MAX_MS / 1000} seconds or shorter — trim it and try again.`,
      );
    }
    if (!video.videoWidth || !video.videoHeight) {
      throw new MediaError(
        'video-unreadable',
        'That video’s format isn’t supported on this device. Try an MP4.',
      );
    }

    // Seek slightly past the start: frame 0 is often black on phone recordings,
    // and a black blur tells a non-Pro viewer nothing at all.
    video.currentTime = Math.min(0.1, video.duration / 2);
    await once(video, 'seeked', PROBE_TIMEOUT_MS, 'video-unreadable');

    const long = Math.max(video.videoWidth, video.videoHeight);
    const scale = Math.min(1, 64 / long);
    const canvas = drawToCanvas(
      video,
      Math.max(1, Math.round(video.videoWidth * scale)),
      Math.max(1, Math.round(video.videoHeight * scale)),
    );

    return {
      durationMs,
      width: video.videoWidth,
      height: video.videoHeight,
      blurhash: blurhashFor(canvas),
      bytes: file.size,
      contentType: file.type,
    };
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

/**
 * Last check before an upload starts, using the same shared rule the callable
 * runs. Returns null when the candidate is fine.
 */
export function checkLimits(
  candidate: MediaCandidate,
  target: MediaTarget = 'media',
): MediaLimitCode | null {
  return mediaLimitViolation(candidate, target);
}

// ---------------------------------------------------------------------------
// BlurHash
// ---------------------------------------------------------------------------

/*
 * A self-contained BlurHash encoder/decoder — the published algorithm, ~120
 * lines, rather than a dependency. The format is worth using as-is because
 * decoders exist everywhere (including native, which matters the day the feed
 * gets a widget), and because its size is fixed and tiny by construction.
 *
 * WHY IT CANNOT LEAK THE IMAGE. The hash is the first 4x3 cosine coefficients
 * of the picture: one average colour plus eleven very low-frequency terms,
 * quantised to a handful of bits each. The whole string is 28 base-83
 * characters — about 178 bits, ~22 bytes, for an image of any size. There is
 * no resolution in it to recover: a face is a smudge, text is a smear. That is
 * the point. The real pixels stay behind storage.rules and a non-Pro client
 * never asks for them.
 */

/** The published base-83 alphabet, in the published order. Do not reorder it. */
const BASE83 =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz#$%*+,-.:;=?@[]^_{|}~';

/** Long edge of the sampling canvas. 32px is ample for 4x3 coefficients. */
const BLURHASH_SAMPLE_PX = 32;

const COMPONENTS_X = 4;
const COMPONENTS_Y = 3;

type CanvasSource = ImageBitmap | HTMLImageElement | HTMLVideoElement | HTMLCanvasElement;

/**
 * Compute the blur string for an image or a video frame.
 *
 * Store the result on the post/message/profile document. It must be present:
 * a media document without one is an empty grey hole for every non-Pro
 * viewer, which is most viewers.
 */
export function blurhashFor(source: CanvasSource, componentX = COMPONENTS_X, componentY = COMPONENTS_Y): string {
  const { width, height } = sourceSize(source);
  const scale = Math.min(1, BLURHASH_SAMPLE_PX / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = source instanceof HTMLCanvasElement && width <= BLURHASH_SAMPLE_PX * 2
    ? source
    : drawToCanvas(source, w, h);
  const ctx = context2d(canvas);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const hash = encodeBlurhash(data, canvas.width, canvas.height, componentX, componentY);

  // Cheap self-check: the storage contract caps this and a longer string would
  // be silently rejected later, at upload time, with nothing to explain it.
  if (hash.length > BLURHASH_MAX_CHARS) {
    throw new MediaError('encode-failed', 'Couldn’t prepare the preview for that file.');
  }
  return hash;
}

/**
 * Decode a hash into a tiny PNG data URL, for the locked placeholder.
 *
 * Render it stretched to the media's aspect ratio (the MediaRef carries the
 * real width/height) with a CSS blur on top; the underlying image is 32px, so
 * it costs nothing to draw and nothing to fetch.
 *
 * Returns null for anything malformed — a corrupt hash on one post must render
 * as a plain placeholder, never break the feed.
 */
export function blurhashToDataUrl(hash: string, width = 32, height = 32, punch = 1): string | null {
  const pixels = decodeBlurhash(hash, width, height, punch);
  if (!pixels) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const image = ctx.createImageData(width, height);
    image.data.set(pixels);
    ctx.putImageData(image, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function encode83(value: number, length: number): string {
  let result = '';
  for (let i = 1; i <= length; i += 1) {
    const digit = Math.floor(value / 83 ** (length - i)) % 83;
    result += BASE83[digit];
  }
  return result;
}

function decode83(str: string): number {
  let value = 0;
  for (const char of str) {
    const index = BASE83.indexOf(char);
    if (index < 0) return Number.NaN;
    value = value * 83 + index;
  }
  return value;
}

/** Gamma-decode one 0-255 channel into linear light. */
function toLinearChannel(value: number): number {
  const v = value / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/**
 * Gamma-encode linear light back to a 0-255 channel.
 *
 * `trunc(x + 0.5)`, not `round(x + 0.5)` — the published algorithm rounds by
 * truncating the half it just added, and rounding on top of it biases every
 * channel upward by half a level. Third-party decoders will read these hashes;
 * they should not see a brightness we invented.
 */
function toDisplayChannel(value: number): number {
  const v = Math.max(0, Math.min(1, value));
  return v <= 0.0031308
    ? Math.trunc(v * 12.92 * 255 + 0.5)
    : Math.trunc((1.055 * v ** (1 / 2.4) - 0.055) * 255 + 0.5);
}

function signPow(value: number, exp: number): number {
  return Math.sign(value) * Math.abs(value) ** exp;
}

function encodeBlurhash(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  componentX: number,
  componentY: number,
): string {
  const factors: number[][] = [];
  for (let y = 0; y < componentY; y += 1) {
    for (let x = 0; x < componentX; x += 1) {
      const normalisation = x === 0 && y === 0 ? 1 : 2;
      let r = 0;
      let g = 0;
      let b = 0;
      for (let px = 0; px < width; px += 1) {
        for (let py = 0; py < height; py += 1) {
          const basis =
            normalisation *
            Math.cos((Math.PI * x * px) / width) *
            Math.cos((Math.PI * y * py) / height);
          const i = 4 * px + py * width * 4;
          r += basis * toLinearChannel(pixels[i]);
          g += basis * toLinearChannel(pixels[i + 1]);
          b += basis * toLinearChannel(pixels[i + 2]);
        }
      }
      const scale = 1 / (width * height);
      factors.push([r * scale, g * scale, b * scale]);
    }
  }

  const dc = factors[0];
  const ac = factors.slice(1);

  let hash = encode83(componentX - 1 + (componentY - 1) * 9, 1);

  let maximumValue = 1;
  if (ac.length > 0) {
    // The reference implementation takes the maximum of the raw coefficients;
    // this takes the maximum of their MAGNITUDES. Both produce a valid hash —
    // the quantised maximum is stored in the string, so every decoder agrees —
    // but the raw version clamps to zero on an image whose AC terms are all
    // negative (a dark photo with a bright corner), which flattens the blur.
    const actualMaximum = Math.max(...ac.map((f) => Math.max(...f.map(Math.abs))));
    const quantised = Math.floor(Math.max(0, Math.min(82, Math.floor(actualMaximum * 166 - 0.5))));
    maximumValue = (quantised + 1) / 166;
    hash += encode83(quantised, 1);
  } else {
    hash += encode83(0, 1);
  }

  hash += encode83(
    (toDisplayChannel(dc[0]) << 16) + (toDisplayChannel(dc[1]) << 8) + toDisplayChannel(dc[2]),
    4,
  );
  for (const factor of ac) {
    const quant = (v: number): number =>
      Math.floor(Math.max(0, Math.min(18, Math.floor(signPow(v / maximumValue, 0.5) * 9 + 9.5))));
    hash += encode83(quant(factor[0]) * 19 * 19 + quant(factor[1]) * 19 + quant(factor[2]), 2);
  }
  return hash;
}

function decodeBlurhash(
  hash: string,
  width: number,
  height: number,
  punch: number,
): Uint8ClampedArray | null {
  if (typeof hash !== 'string' || hash.length < 6 || hash.length > BLURHASH_MAX_CHARS) return null;
  const sizeFlag = decode83(hash[0]);
  if (!Number.isFinite(sizeFlag)) return null;
  const componentX = (sizeFlag % 9) + 1;
  const componentY = Math.floor(sizeFlag / 9) + 1;
  if (hash.length !== 4 + 2 * componentX * componentY) return null;

  const quantisedMaximum = decode83(hash[1]);
  if (!Number.isFinite(quantisedMaximum)) return null;
  const maximumValue = ((quantisedMaximum + 1) / 166) * Math.max(0, punch);

  const colours: number[][] = [];
  const dcValue = decode83(hash.substring(2, 6));
  if (!Number.isFinite(dcValue)) return null;
  colours.push([
    toLinearChannel(dcValue >> 16),
    toLinearChannel((dcValue >> 8) & 255),
    toLinearChannel(dcValue & 255),
  ]);

  for (let i = 1; i < componentX * componentY; i += 1) {
    const value = decode83(hash.substring(4 + i * 2, 6 + i * 2));
    if (!Number.isFinite(value)) return null;
    const quantR = Math.floor(value / (19 * 19));
    const quantG = Math.floor(value / 19) % 19;
    const quantB = value % 19;
    colours.push([
      signPow((quantR - 9) / 9, 2) * maximumValue,
      signPow((quantG - 9) / 9, 2) * maximumValue,
      signPow((quantB - 9) / 9, 2) * maximumValue,
    ]);
  }

  const out = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let r = 0;
      let g = 0;
      let b = 0;
      for (let j = 0; j < componentY; j += 1) {
        for (let i = 0; i < componentX; i += 1) {
          const basis =
            Math.cos((Math.PI * x * i) / width) * Math.cos((Math.PI * y * j) / height);
          const colour = colours[i + j * componentX];
          r += colour[0] * basis;
          g += colour[1] * basis;
          b += colour[2] * basis;
        }
      }
      const p = 4 * x + y * width * 4;
      out[p] = toDisplayChannel(r);
      out[p + 1] = toDisplayChannel(g);
      out[p + 2] = toDisplayChannel(b);
      out[p + 3] = 255;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Canvas / decode plumbing
// ---------------------------------------------------------------------------

interface DecodedSource {
  image: CanvasSource;
  width: number;
  height: number;
  release(): void;
}

async function decodeOriented(file: Blob): Promise<DecodedSource> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        image: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      // Falls through to the <img> path: either the browser rejected the
      // options bag or the file is not decodable at all. The second case fails
      // again below, with a message the user can act on.
    }
  }

  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;
  try {
    await once(img, 'load', PROBE_TIMEOUT_MS, 'image-unreadable');
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
  return {
    image: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    release: () => URL.revokeObjectURL(url),
  };
}

function sourceSize(source: CanvasSource): { width: number; height: number } {
  if (source instanceof HTMLVideoElement) {
    return { width: source.videoWidth, height: source.videoHeight };
  }
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
}

function drawToCanvas(source: CanvasSource, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = context2d(canvas);
  // One step, high-quality filtering. Repeated halving would alias slightly
  // less on a very large source, but downscaleImage may draw up to four times
  // while it hunts for a size that fits, and four halving chains on a 12MP
  // photo is real jank on a low-end Android for a difference nobody can see at
  // 256px.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new MediaError('canvas-unavailable', 'This device can’t prepare images for upload.');
  }
  return ctx;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new MediaError('encode-failed', 'That image couldn’t be prepared for upload.'));
      },
      type,
      quality,
    );
  });
}

let webpSupport: boolean | null = null;

/**
 * Safari before 14 and a few embedded WebViews silently hand back a PNG when
 * asked for WebP — which would be several times the byte target and would fail
 * the byte cap in storage.rules with a completely opaque error. Detect once and
 * encode JPEG instead where that is the case.
 */
function supportsWebp(): boolean {
  if (webpSupport !== null) return webpSupport;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    webpSupport = false;
  }
  return webpSupport;
}

/** One-shot event wait with a deadline, so a bad file cannot hang a screen. */
function once(
  target: HTMLImageElement | HTMLVideoElement,
  event: string,
  timeoutMs: number,
  code: MediaClientErrorCode,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const fail = (): void => {
      cleanup();
      reject(
        new MediaError(
          code,
          code === 'video-unreadable'
            ? 'That video couldn’t be read on this device. Try an MP4.'
            : 'That image couldn’t be opened. Try a different one.',
        ),
      );
    };
    const done = (): void => {
      cleanup();
      resolve();
    };
    const timer = setTimeout(fail, timeoutMs);
    function cleanup(): void {
      clearTimeout(timer);
      target.removeEventListener(event, done);
      target.removeEventListener('error', fail);
    }
    target.addEventListener(event, done, { once: true });
    target.addEventListener('error', fail, { once: true });
  });
}

/** Re-exported so screens import one module for the whole media contract. */
export {
  AVATAR_MAX_BYTES,
  AVATAR_MAX_PX,
  MEDIA_IMAGE_CONTENT_TYPES,
  MEDIA_MAX_BYTES,
  MEDIA_MAX_PX,
  VIDEO_MAX_BYTES,
  VIDEO_MAX_MS,
};
